import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Cloud, ArrowLeft, Mail, Lock, Loader2, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTH_BACKGROUND_CLASS = "fixed inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(0,242,234,0.20),transparent_28%),radial-gradient(circle_at_80%_10%,rgba(255,0,128,0.16),transparent_24%),linear-gradient(135deg,#050505,#111827_45%,#020617)]";

export default function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get("token");
  const navigate = useNavigate();
  
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      setEmailSent(true);
      
      // Check if email was sent or if we got a fallback token
      if (response.data.reset_token) {
        toast.info(
          <div>
            <p>Email service unavailable</p>
            <p className="text-xs mt-1 text-white/60">Token: {response.data.reset_token}</p>
            <p className="text-xs text-white/60">Use this to reset your password</p>
          </div>,
          { duration: 15000 }
        );
      } else {
        toast.success("Password reset email sent! Check your inbox.");
      }
    } catch (error) {
      toast.error("Failed to send reset email");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      setResetComplete(true);
      toast.success("Password reset successfully!");
      setTimeout(() => navigate("/login"), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setIsLoading(false);
    }
  };

  // Reset password form (when token is present)
  if (resetToken) {
    if (resetComplete) {
      return (
        <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden">
          <div className={AUTH_BACKGROUND_CLASS} />
          <div className="fixed inset-0 bg-black/60" />
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative z-10 glass rounded-3xl p-8 text-center max-w-md"
          >
            <div className="w-16 h-16 rounded-full bg-[#ccff00]/20 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-[#ccff00]" />
            </div>
            <h2 className="text-xl font-bold mb-2">Password Reset!</h2>
            <p className="text-white/60 mb-4">Your password has been successfully reset. Redirecting to login...</p>
          </motion.div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden">
        <div className={AUTH_BACKGROUND_CLASS} />
        <div className="fixed inset-0 bg-black/60" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 w-full max-w-md"
        >
          <div className="glass rounded-3xl p-8 shadow-2xl">
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="p-3 rounded-2xl bg-gradient-to-br from-[#00f2ea]/20 to-transparent">
                <Cloud className="w-8 h-8 text-[#00f2ea]" />
              </div>
              <h1 className="text-3xl font-black tracking-tight">TheForecastTracker</h1>
            </div>

            <h2 className="text-xl font-semibold mb-2 text-center">Reset Password</h2>
            <p className="text-white/50 text-center mb-8">Enter your new password</p>

            <form onSubmit={handleResetPassword} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="Min. 6 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="new-password-input"
                    className="pl-12 bg-white/5 border-white/10 h-12 rounded-xl"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    data-testid="confirm-password-input"
                    className="pl-12 bg-white/5 border-white/10 h-12 rounded-xl"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                data-testid="reset-password-btn"
                className="w-full h-12 rounded-xl bg-white text-black hover:bg-white/90 font-semibold"
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Reset Password"}
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  // Request reset form
  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden">
        <div className={AUTH_BACKGROUND_CLASS} />
        <div className="fixed inset-0 bg-black/60" />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative z-10 glass rounded-3xl p-8 text-center max-w-md"
        >
          <div className="w-16 h-16 rounded-full bg-[#00f2ea]/20 flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-[#00f2ea]" />
          </div>
          <h2 className="text-xl font-bold mb-2">Check Your Email</h2>
          <p className="text-white/60 mb-4">
            If an account exists with that email, you'll receive a password reset link.
          </p>
          <p className="text-sm text-white/40 mb-6">
            (For demo: check the toast notification for your reset token)
          </p>
          <Link to="/login">
            <Button variant="ghost" className="rounded-xl">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden">
      <div className={AUTH_BACKGROUND_CLASS} />
      <div className="fixed inset-0 bg-black/60" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="glass rounded-3xl p-8 shadow-2xl">
          <Link to="/login" className="inline-flex items-center text-white/60 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to login
          </Link>

          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="p-3 rounded-2xl bg-gradient-to-br from-[#00f2ea]/20 to-transparent">
              <Cloud className="w-8 h-8 text-[#00f2ea]" />
            </div>
            <h1 className="text-3xl font-black tracking-tight">TheForecastTracker</h1>
          </div>

          <h2 className="text-xl font-semibold mb-2 text-center">Forgot Password?</h2>
          <p className="text-white/50 text-center mb-8">Enter your email to receive a reset link</p>

          <form onSubmit={handleRequestReset} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="forgot-email-input"
                  className="pl-12 bg-white/5 border-white/10 h-12 rounded-xl"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              data-testid="send-reset-btn"
              className="w-full h-12 rounded-xl bg-white text-black hover:bg-white/90 font-semibold"
            >
              {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send Reset Link"}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
